import React, { useState, useCallback, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import type { AxiosProgressEvent } from 'axios';
import { palette } from '@/styles/figma-tokens';
import StepCard from '../shared/StepCard';
import DropZone from '../shared/DropZone';
import { createCourse, updateCourse } from '../../../../services/courses';
import { uploadFile, getSignedUrl } from '../../../../services/uploads';
import type { CourseLayout } from '@/types/course';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_IMAGE_SIZE_MB = 5;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ─── Inline field styles (Figma spec) ────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid rgb(var(--color-border-primary) / 0.45)',
  borderRadius: '8px',
  padding: '14px 16px',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '15px',
  lineHeight: '24px',
  color: 'rgb(var(--color-text-primary))',
  background: 'rgb(var(--color-surface-primary))',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: palette.error['500'],
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '18px',
  color: 'rgb(var(--color-text-secondary))',
  marginBottom: '6px',
};

const errorTextStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  lineHeight: '18px',
  color: palette.error['500'],
  marginTop: '4px',
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CourseInitialData {
  title: string;
  description: string | null;
  image_url: string | null;
  estimated_duration_minutes: number | null;
  layout?: CourseLayout;
}

interface CourseDetailsStepProps {
  /** null = create mode, non-null = edit mode */
  courseId: string | null;
  /** Pre-populated data for edit mode */
  initialData?: CourseInitialData;
  disabled?: boolean;
  onSaved: (courseId: string) => void;
  onCancel?: () => void;
  /** Stagger delay forwarded to StepCard (seconds). */
  entranceDelay?: number;
}

interface FormErrors {
  title?: string;
  estimatedDurationMinutes?: string;
}

// ─── Image preview state ──────────────────────────────────────────────────────

interface ImageState {
  /** The S3 key stored in form state (sent to the API) */
  key: string | null;
  /** A local blob URL or signed S3 URL used only for preview rendering */
  previewUrl: string | null;
  /** File name shown in the preview bar */
  fileName: string | null;
  /** File size in bytes shown in the preview bar */
  fileSize: number | null;
  uploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
}

// ─── Layout preview skeletons ─────────────────────────────────────────────────
// Tiny mockups so the trainer sees the difference between the two layouts.

function CardsLayoutSkeleton() {
  return (
    <div className="flex gap-1.5" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex-1 rounded-md bg-text-tertiary/10 overflow-hidden">
          <div className="h-7 bg-primary-500/25" />
          <div className="p-1.5 space-y-1">
            <div className="h-1 rounded-full bg-text-tertiary/30 w-3/4" />
            <div className="h-1 rounded-full bg-text-tertiary/20 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ListLayoutSkeleton() {
  return (
    <div className="flex flex-col gap-1.5" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2 rounded-md bg-text-tertiary/10 p-1.5">
          <div className="w-4 h-4 rounded bg-primary-500/25 shrink-0" />
          <div className="h-1.5 rounded-full bg-text-tertiary/30 flex-1" />
        </div>
      ))}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * CourseDetailsStep — Step 1 of the course creation / editing wizard.
 *
 * Renders inside a StepCard shell. Handles create (POST) and edit (PUT) modes,
 * image upload via DropZone, and basic validation.
 */
export default function CourseDetailsStep({
  courseId,
  initialData,
  disabled = false,
  onSaved,
  onCancel,
  entranceDelay,
}: CourseDetailsStepProps) {
  // ── Form fields ──────────────────────────────────────────────────────────
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [durationMinutes, setDurationMinutes] = useState(
    initialData?.estimated_duration_minutes ? String(initialData.estimated_duration_minutes) : ''
  );
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [layout, setLayout] = useState<CourseLayout>(initialData?.layout ?? 'cards');
  const [errors, setErrors] = useState<FormErrors>({});

  // ── Image state ──────────────────────────────────────────────────────────
  const [image, setImage] = useState<ImageState>({
    key: initialData?.image_url ?? null,
    previewUrl: null,
    fileName: initialData?.image_url ? 'Header picture' : null,
    fileSize: null,
    uploading: false,
    uploadProgress: 0,
    uploadError: null,
  });

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  // In edit mode (initialData present) the step already has persisted data —
  // start collapsed so the user explicitly opts in to editing.
  const [saved, setSaved] = useState(!!initialData);

  // Track whether a signed-url fetch for edit-mode preview is in flight
  const signedUrlAbortRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // ── Edit mode: resolve existing image key to a signed URL for preview ────
  useEffect(() => {
    const imageKey = initialData?.image_url;
    if (!imageKey) return;

    let cancelled = false;
    signedUrlAbortRef.current = { cancelled: false };

    getSignedUrl({ key: imageKey }).then((result) => {
      if (!cancelled) {
        setImage((prev) => ({
          ...prev,
          previewUrl: result.url,
        }));
      }
    }).catch(() => {
      // Key might be invalid or expired — ignore
    });

    return () => {
      cancelled = true;
      signedUrlAbortRef.current.cancelled = true;
    };
  }, [initialData?.image_url]);

  // ── Validation ───────────────────────────────────────────────────────────

  const validate = (): FormErrors => {
    const errs: FormErrors = {};

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      errs.title = 'Title is required';
    } else if (trimmedTitle.length < 3) {
      errs.title = 'Title must be at least 3 characters';
    } else if (trimmedTitle.length > 200) {
      errs.title = 'Title must not exceed 200 characters';
    }

    const duration = Number(durationMinutes);
    if (!durationMinutes.trim()) {
      errs.estimatedDurationMinutes = 'Duration is required';
    } else if (!Number.isFinite(duration) || duration < 1) {
      errs.estimatedDurationMinutes = 'At least 1 minute';
    } else if (duration > 9999) {
      errs.estimatedDurationMinutes = 'Maximum 9999 minutes';
    }

    return errs;
  };

  // ── Image upload ─────────────────────────────────────────────────────────

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      // Client-side validation
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setImage((prev) => ({
          ...prev,
          uploadError: 'Only JPG, PNG, and WebP images are allowed',
        }));
        return;
      }

      // Create a local preview immediately so the user sees feedback
      const localPreview = URL.createObjectURL(file);
      setImage({
        key: null,
        previewUrl: localPreview,
        fileName: file.name,
        fileSize: file.size,
        uploading: true,
        uploadProgress: 0,
        uploadError: null,
      });

      try {
        // Use the existing courseId if we are editing; otherwise a placeholder
        // UUID so the upload can be associated before the course record exists.
        const resourceId = courseId ?? crypto.randomUUID();
        const result = await uploadFile(
          { file, resource_type: 'course_cover', resource_id: resourceId },
          {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const pct = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 100)
              );
              setImage((prev) => ({ ...prev, uploadProgress: pct }));
            },
          }
        );

        setImage((prev) => ({
          ...prev,
          key: result.key,
          uploading: false,
          uploadProgress: 100,
        }));
      } catch (err: any) {
        URL.revokeObjectURL(localPreview);
        setImage({
          key: null,
          previewUrl: null,
          fileName: null,
          fileSize: null,
          uploading: false,
          uploadProgress: 0,
          uploadError: err.response?.data?.message ?? 'Upload failed. Please try again.',
        });
      }
    },
    [courseId]
  );

  const handleRemoveImage = () => {
    if (image.previewUrl) {
      URL.revokeObjectURL(image.previewUrl);
    }
    setImage({
      key: null,
      previewUrl: null,
      fileName: null,
      fileSize: null,
      uploading: false,
      uploadProgress: 0,
      uploadError: null,
    });
  };

  // ── Save handler ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const sanitize = (value: string) =>
      DOMPurify.sanitize(value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

    const payload = {
      title: sanitize(title),
      description: sanitize(description) || null,
      image_url: image.key ?? null,
      estimated_duration_minutes: Number(durationMinutes),
      layout,
    };

    setSaving(true);
    try {
      if (courseId) {
        await updateCourse(courseId, payload);
        setSaved(true);
        onSaved(courseId);
      } else {
        const course = await createCourse(payload);
        setSaved(true);
        onSaved(course.id);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? 'Failed to save course. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const trimmedTitle = title.trim();
  const durationValue = Number(durationMinutes);
  const summary =
    saved && trimmedTitle && Number.isFinite(durationValue) && durationValue > 0
      ? `${trimmedTitle} · ${durationValue} min`
      : undefined;

  return (
    <StepCard
      stepNumber={1}
      title="Basics"
      disabled={disabled}
      onSave={handleSave}
      onCancel={onCancel}
      saving={saving}
      saved={saved}
      summary={summary}
      entranceDelay={entranceDelay}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Row 1: Course name + Duration */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

          {/* Course name — flex-1 */}
          <div style={{ flex: 1 }}>
            <label htmlFor="course-title" style={labelStyle}>
              Add Course Name
            </label>
            <input
              id="course-title"
              type="text"
              placeholder="Add Course Name"
              value={title}
              maxLength={200}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
              }}
              style={errors.title ? inputErrorStyle : inputStyle}
              onFocus={(e) => {
                (e.currentTarget as HTMLInputElement).style.borderColor = errors.title
                  ? palette.error['500']
                  : 'rgb(var(--color-primary))';
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLInputElement).style.borderColor = errors.title
                  ? palette.error['500']
                  : 'rgb(var(--color-border-primary) / 0.45)';
              }}
            />
            {errors.title && (
              <p role="alert" style={errorTextStyle}>
                {errors.title}
              </p>
            )}
          </div>

          {/* Duration — fixed 200px */}
          <div style={{ width: '200px', flexShrink: 0 }}>
            <label htmlFor="course-duration" style={labelStyle}>
              Duration (Minutes)
            </label>
            <input
              id="course-duration"
              type="number"
              placeholder="e.g. 60"
              min={1}
              max={9999}
              value={durationMinutes}
              onChange={(e) => {
                setDurationMinutes(e.target.value);
                if (errors.estimatedDurationMinutes)
                  setErrors((prev) => ({ ...prev, estimatedDurationMinutes: undefined }));
              }}
              style={errors.estimatedDurationMinutes ? inputErrorStyle : inputStyle}
              onFocus={(e) => {
                (e.currentTarget as HTMLInputElement).style.borderColor =
                  errors.estimatedDurationMinutes ? palette.error['500'] : 'rgb(var(--color-primary))';
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLInputElement).style.borderColor =
                  errors.estimatedDurationMinutes ? palette.error['500'] : 'rgb(var(--color-border-primary) / 0.45)';
              }}
            />
            {errors.estimatedDurationMinutes && (
              <p role="alert" style={errorTextStyle}>
                {errors.estimatedDurationMinutes}
              </p>
            )}
          </div>
        </div>

        {/* Row 2: Description textarea */}
        <div>
          <label htmlFor="course-description" style={labelStyle}>
            Course Description
          </label>
          <textarea
            id="course-description"
            placeholder="Course Description"
            value={description}
            rows={4}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: '96px',
            }}
            onFocus={(e) => {
              (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgb(var(--color-primary))';
            }}
            onBlur={(e) => {
              (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgb(var(--color-border-primary) / 0.45)';
            }}
          />
        </div>

        {/* Row: Section layout */}
        <div>
          <label style={labelStyle}>Section layout</label>
          <p className="text-xs text-text-secondary mb-2 -mt-0.5">How learners browse your course.</p>
          <div className="flex flex-col gap-3">
            {([
              {
                value: 'cards',
                title: 'Visual cards',
                desc: 'Each section shows a large header picture (a colored placeholder if there’s no picture).',
              },
              {
                value: 'list',
                title: 'Simple list',
                desc: 'A clean, numbered table of contents. Header pictures appear as small thumbnails when you’ve added them.',
              },
            ] as const).map((opt) => {
              const active = layout === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setLayout(opt.value)}
                  aria-pressed={active}
                  className={`flex items-center gap-4 text-left rounded-2xl border-2 p-4 transition-colors ${
                    active
                      ? 'border-primary-500 bg-primary-500/[0.06]'
                      : 'border-border-primary/30 hover:border-primary-500/50'
                  }`}
                >
                  {/* Text column */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          active ? 'border-primary-500' : 'border-border-primary'
                        }`}
                      >
                        {active && <span className="w-2 h-2 rounded-full bg-primary-500" />}
                      </span>
                      <span className="text-sm font-bold text-text-primary">{opt.title}</span>
                    </div>
                    <p className="mt-1.5 text-xs text-text-secondary">{opt.desc}</p>
                  </div>

                  {/* Preview column — only for the selected option; the row
                      expands to make room for it. */}
                  {active && (
                    <div className="w-2/5 max-w-[220px] shrink-0 animate-fade-in">
                      {opt.value === 'cards' ? <CardsLayoutSkeleton /> : <ListLayoutSkeleton />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-text-tertiary">
            You can switch this anytime — it only changes how sections are displayed, not your content.
          </p>
        </div>

        {/* Row 3: Cover image */}
        <div>
          {image.previewUrl ? (
            /* Preview state — show thumbnail + controls */
            <div
              style={{
                borderRadius: '16px',
                border: '1px solid rgb(var(--color-border-primary) / 0.25)',
                overflow: 'hidden',
                background: 'rgb(var(--color-surface-primary))',
              }}
            >
              {/* Thumbnail */}
              <div
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  overflow: 'hidden',
                  background: 'rgb(var(--color-surface-elevated))',
                }}
              >
                <img
                  src={image.previewUrl}
                  alt="Header picture preview"
                  style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                />
              </div>

              {/* File info + remove */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  gap: '12px',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgb(var(--color-text-primary))',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {image.fileName}
                  </p>
                  {image.fileSize !== null && (
                    <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '12px',
                        color: 'rgb(var(--color-text-secondary))',
                        margin: '2px 0 0',
                      }}
                    >
                      {(image.fileSize / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={image.uploading}
                  style={{
                    flexShrink: 0,
                    background: 'none',
                    border: 'none',
                    padding: '4px 8px',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: palette.error['500'],
                    cursor: image.uploading ? 'not-allowed' : 'pointer',
                    opacity: image.uploading ? 0.5 : 1,
                  }}
                >
                  Remove
                </button>
              </div>

              {/* Upload progress bar */}
              {image.uploading && (
                <div style={{ padding: '0 16px 12px' }}>
                  <div
                    style={{
                      height: '4px',
                      background: 'rgb(var(--color-primary) / 0.15)',
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${image.uploadProgress}%`,
                        background: 'rgb(var(--color-primary))',
                        borderRadius: '2px',
                        transition: 'width 0.2s',
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      color: 'rgb(var(--color-text-secondary))',
                      margin: '4px 0 0',
                    }}
                  >
                    Uploading... {image.uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Drop zone */
            <DropZone
              onFilesSelected={handleFilesSelected}
              accept="image/jpeg,image/png,image/webp"
              maxSizeMB={MAX_IMAGE_SIZE_MB}
              label="Drop or select Header Picture for the course"
            />
          )}

          {/* Upload error */}
          {image.uploadError && (
            <p role="alert" style={{ ...errorTextStyle, marginTop: '8px' }}>
              {image.uploadError}
            </p>
          )}
        </div>
      </div>
    </StepCard>
  );
}
